import { useGetDeletePreview, useRemoveKeyPerson } from '@commenda/api';
import { DatePicker, Input, Label } from '@commenda/kakashi';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { useEffect, useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { DeleteConfirmationModal } from '../../../molecules/modals/ConfirmationModal';

export const RemoveKeyPersonModal = ({
  isOpen,
  onClose,
  personId,
  personName,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  personId: number | null;
  personName: string;
  onSuccess?: () => void;
}) => {
  const { setNotification } = useNotification();
  const { mutateAsync: removeKeyPerson } = useRemoveKeyPerson();
  const { data: preview } = useGetDeletePreview(personId, { enabled: isOpen });

  const [confirmName, setConfirmName] = useState('');
  const [removalDate, setRemovalDate] = useState<string | undefined>(undefined);

  // Prefill removal date with today so an unchanged confirm archives as of now.
  useEffect(() => {
    if (isOpen) {
      setConfirmName('');
      setRemovalDate(NonTimezoneSensitiveDate.now().toISO8601String());
    }
  }, [isOpen]);

  const trimmedName = personName.trim();
  const canRemove = trimmedName.length > 0 && confirmName.trim() === trimmedName;

  const handleConfirm = async () => {
    if (!personId || !canRemove) return;
    try {
      await removeKeyPerson({ personId, removalDate });
      setNotification({
        show: true,
        title: 'Key person removed',
        message: 'The key person has been successfully removed.',
        valid: true,
      });
      onSuccess?.();
    } catch (error) {
      setNotification({
        show: true,
        title: 'Failed to remove key person',
        message: error instanceof Error ? error.message : 'Please try again.',
        valid: false,
      });
      // Re-throw so the modal stays open on failure (it auto-closes on a resolved confirm).
      throw error;
    }
  };

  const entityCount = preview?.entities.length ?? 0;
  const roleCount = preview?.activeRoleAssignmentCount ?? 0;

  const message = (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-[14px] text-text-primary">
        <p className="txt-small-regular">
          Removing{' '}
          <span className="txt-small-bold">
            {trimmedName || 'this key person'}
          </span>{' '}
          archives this person&apos;s profile and unlinks it from every record
          tied to them across your workspace. This affects:
        </p>
        <ul className="list-disc pl-xl">
          <li className="txt-small-regular">
            <span className="txt-small-bold">{entityCount}</span>{' '}
            {entityCount === 1 ? 'entity' : 'entities'}
          </li>
          <li className="txt-small-regular">
            <span className="txt-small-bold">{roleCount}</span>{' '}
            {roleCount === 1 ? 'role' : 'roles'}
          </li>
        </ul>
        <p className="txt-small-regular">
          This action cannot be undone. Archived records can&apos;t be restored.
        </p>
        <p className="txt-small-regular">
          Enter the key person&apos;s name to confirm.
        </p>
      </div>

      <div className="flex flex-col gap-xs">
        <Label htmlFor="kp-removal-date">Removal date</Label>
        <DatePicker
          date={removalDate}
          setDate={setRemovalDate}
          placeholder="Removal date"
        />
      </div>

      <Input
        id="kp-remove-confirm"
        value={confirmName}
        onChange={(e) => setConfirmName(e.target.value)}
        placeholder={`Type "${trimmedName}" to confirm`}
        autoComplete="off"
        spellCheck={false}
      />
    </div>
  );

  return (
    <DeleteConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleConfirm}
      title="Remove key person"
      message={message}
      confirmButtonText="Remove key person"
      confirmDisabled={!canRemove}
    />
  );
};
